/* ============================================================
   DATA RAWAT INAP

   Letakkan di: app/layanan/rawat-inap/rawat-inap.js

   Seluruh nomor, nama kelas kamar, harga, dan keterangan kapasitas
   berasal dari halaman lama di rsjmc.id/rawat-inap.
   ============================================================ */

/* ============================================================
   FOTO KAMAR - UBAH DI SATU TEMPAT SAJA

   Foto tidak muncul biasanya karena satu dari dua sebab: ekstensinya
   berbeda, atau namanya berbeda. Keduanya kini diatur oleh dua
   konstanta di bawah, bukan disebar di lima baris path.

   LANGKAH 1 - lihat nama berkas yang sebenarnya. Di Command Prompt,
   dari dalam folder `main`:

       dir public\images\rwtinap*

   LANGKAH 2 - sesuaikan dua konstanta di bawah.

   Contoh hasil dan cara menyesuaikannya:

     rwtinap1.png ... rwtinap5.png
       -> EKSTENSI = "png",  NAMA_FOTO tetap

     rwtinap-1.jpg ... rwtinap-5.jpg
       -> EKSTENSI = "jpg",  NAMA_FOTO = ["rwtinap-1", ... ]

     rwtinap1-5.jpg   (ternyata SATU berkas)
       -> NAMA_FOTO = ["rwtinap1-5"]

   Huruf besar-kecil harus sama persis. Windows tidak membedakan
   "RwtInap1.JPG" dari "rwtinap1.jpg", tapi server produksi
   membedakan - berkas yang tampil di komputermu bisa hilang begitu
   situsnya online.

   Berkas yang tetap gagal dimuat disembunyikan sendiri, jadi kalau
   ternyata hanya ada tiga dari lima, dua sisanya tidak menyisakan
   kotak rusak di dalam galeri.
   ============================================================ */

const EKSTENSI = "jpg";

const NAMA_FOTO = ["rwtinap1", "rwtinap2", "rwtinap3", "rwtinap4", "rwtinap5"];

/* ⚠️ ALT-NYA MASIH UMUM untuk kelima foto. Idealnya tiap foto
   menyebut kamar kelas apa yang ditampilkannya - berguna bagi
   pengguna pembaca layar, dan bagi calon pasien yang ingin tahu rupa
   kelas tertentu. Saya tidak tahu foto mana menampilkan kelas mana.

   Kalau kamu tahu, ubah NAMA_FOTO jadi array objek dan isi alt-nya
   masing-masing. */
export const FOTO = NAMA_FOTO.map((nama) => ({
  src: `/images/${nama}.${EKSTENSI}`,
  alt: {
    id: "Kamar rawat inap RS Jakarta Medical Center.",
    en: "Inpatient room at Jakarta Medical Center Hospital.",
  },
}));

/* ============================================================
   TANGGAL PENINJAUAN HARGA

   Peringatan "harga dapat berubah" tanpa tanggal tidak membantu
   siapa pun - pengunjung tidak bisa menilai apakah angkanya masih
   masuk akal.

   SETEL ULANG setiap kali harga ditinjau. Kalau dibiarkan
   bertahun-tahun, tanggal ini justru jadi bukti bahwa harganya sudah
   lama tidak diperbarui - dan itu memang informasi yang jujur.
   ============================================================ */
export const DITINJAU = { id: "Agustus 2026", en: "August 2026" };

/* ============================================================
   NOMOR

   Admin Rawat Inap jadi tombol utama - sama dengan halaman lama, dan
   memang tepat: yang tahu kamar kelas mana sedang kosong adalah admin
   rawat inap, dan ketersediaan berubah setiap hari.

   ⚠️ NOMOR 0811-9728-656 HANYA MUNCUL DI HALAMAN INI, tidak ada di
   app/data/contact.js maupun di Footer. Pastikan masih aktif dan
   masih dipegang admin rawat inap.

   Nomor hunting sengaja TIDAK diberi medan `wa` - ia sambungan
   telepon ke operator, bukan nomor WhatsApp.
   ============================================================ */
export const RAWAT_INAP = {
  telepon: [
    {
      label: { id: "Admin Rawat Inap", en: "Inpatient Admissions" },
      tampil: "0811-9728-656",
      tel: "+628119728656",
      wa: "628119728656",
      pesanWa: {
        id: "Halo Admin Rawat Inap RS Jakarta Medical Center,\n\nSaya ingin menanyakan ketersediaan kamar rawat inap.\n\nMohon informasinya. Terima kasih.",
        en: "Hello RS Jakarta Medical Center inpatient admissions,\n\nI would like to ask about room availability.\n\nThank you for your help.",
      },
      utama: true,
    },
    {
      label: { id: "Customer Service", en: "Customer Service" },
      tampil: "0811-901-874",
      tel: "+62811901874",
      wa: "62811901874",
      pesanWa: {
        id: "Halo RS Jakarta Medical Center,\n\nSaya ingin menanyakan mengenai layanan rawat inap.\n\nMohon bantuannya. Terima kasih.",
        en: "Hello RS Jakarta Medical Center,\n\nI would like to ask about the inpatient service.\n\nThank you for your help.",
      },
      utama: false,
    },
    {
      label: { id: "Hunting", en: "Main line" },
      tampil: "(021) 798 0888",
      tel: "+62217980888",
      utama: false,
    },
  ],
};

/* ============================================================
   KELAS KAMAR - DIKELOMPOKKAN PER KATEGORI

   Dulu ini satu array datar berisi kamar dewasa saja. Sekarang
   berupa daftar KATEGORI, masing-masing berisi daftar kamarnya -
   karena situs lama memang membaginya begitu, dan karena nama kelas
   BERULANG antar kategori.

   "Kelas Satu" ada di kamar dewasa (Rp 370.000) DAN di kamar anak
   (Rp 315.000). Tanpa pengelompokan, dua entri bernama sama dengan
   harga berbeda akan terbaca seperti kekeliruan.

   Pengelompokan ini juga yang membuat tombol "Tanya ketersediaan"
   bisa menyebut kategorinya di pesan WhatsApp. Tanpa itu, admin
   menerima pesan "menanyakan ketersediaan kamar Kelas Satu" dan harus
   bertanya balik: dewasa atau anak?

   MENAMBAH KATEGORI cukup menambah satu objek di array ini -
   komponennya merender berapa pun jumlahnya tanpa perlu disunting.

   ⚠️ KATEGORI KETIGA BELUM ADA DI SINI. Halaman lama punya bagian
   "Perawatan Khusus" di bawah Kamar Anak, tapi isinya tidak terlihat
   di bagian yang bisa saya baca. Kalau isinya sudah ada, tambahkan:

       {
         key: "khusus",
         judul: { id: "Perawatan Khusus", en: "Special Care" },
         kamar: [ ... ],
       },

   ============================================================
   ⚠️ DUA HARGA KAMAR DEWASA SAYA PERBAIKI. PERIKSA KEBENARANNYA.

   1. SUPER VIP ROOM
      Situs lama : "Mulai dari Rp 1.080.000 - Rp 1.025.000"
      Sekarang   : "Mulai dari Rp 1.025.000 - Rp 1.080.000"
      Urutannya dibalik - rentang menurun hampir pasti salah ketik.
      Kemungkinan lain: kedua angka itu untuk dua tipe kamar berbeda,
      bukan satu rentang.

   2. KELAS UTAMA
      Situs lama : "Rp 580.00 - Rp 765.000"
      Sekarang   : "Mulai dari Rp 580.000 - Rp 765.000"
      Ditambah satu angka nol. "Rp 580.00" bukan angka rupiah yang
      sah, dan 580.000 masuk akal karena berada di antara VIP
      (815.000) dan Kelas Satu (475.000).

   Harga kamar anak disalin apa adanya - ketujuhnya sudah berbentuk
   wajar dan tidak perlu ditafsirkan.

   ============================================================
   FIELD PER KAMAR
     nama      - nama kelasnya
     harga     - ditulis sebagai teks, bukan angka. Sebagian berupa
                 rentang, dan formatnya sudah sesuai kebiasaan
                 Indonesia.
     kapasitas - jumlah orang sekamar. Kosongkan kalau tidak ada.
     periksa   - setel true kalau angkanya belum bisa dipastikan.
                 Harganya disembunyikan dan diganti ajakan menghubungi
                 Admin Rawat Inap. Lebih baik tidak menampilkan harga
                 daripada menampilkan harga keliru.
   ============================================================ */
export const KATEGORI_KAMAR = [
  {
    key: "dewasa",
    judul: { id: "Kamar Dewasa", en: "Adult Rooms" },
    kamar: [
      {
        nama: { id: "Suite Room", en: "Suite Room" },
        harga: { id: "Mulai dari Rp 1.600.000", en: "From IDR 1,600,000" },
      },
      {
        nama: { id: "Super VIP Room", en: "Super VIP Room" },
        harga: { id: "Mulai dari Rp 1.025.000 - Rp 1.080.000", en: "From IDR 1,025,000 - IDR 1,080,000" },
      },
      {
        nama: { id: "VIP Room", en: "VIP Room" },
        harga: { id: "Mulai dari Rp 815.000", en: "From IDR 815,000" },
      },
      {
        nama: { id: "Kelas Utama", en: "Main Class" },
        harga: { id: "Mulai dari Rp 580.000 - Rp 765.000", en: "From IDR 580,000 - IDR 765,000" },
      },
      {
        nama: { id: "Kelas Satu", en: "Class I" },
        harga: { id: "Mulai dari Rp 370.000 - Rp 475.000", en: "From IDR 370,000 - IDR 475,000" },
        kapasitas: { id: "Sekamar berdua", en: "Two patients per room" },
      },
      {
        nama: { id: "Kelas Dua", en: "Class II" },
        harga: { id: "Mulai dari Rp 265.000 - Rp 315.000", en: "From IDR 265,000 - IDR 315,000" },
        kapasitas: { id: "Sekamar bertiga", en: "Three patients per room" },
      },
      {
        nama: { id: "Kelas Tiga", en: "Class III" },
        harga: { id: "Mulai dari Rp 160.000", en: "From IDR 160,000" },
        kapasitas: { id: "Sekamar lima sampai tujuh orang", en: "Five to seven patients per room" },
      },
      {
        nama: { id: "Kamar Isolasi Covid", en: "Covid Isolation Room" },

        /* ⚠️ MASIH RELEVAN? Kelas ini ada di situs lama sejak masa
           pandemi. Kalau kamar isolasinya kini dipakai untuk penyakit
           menular secara umum, namanya sebaiknya jadi "Kamar Isolasi"
           saja - lebih tepat, dan tidak membuat halaman terlihat
           tertinggal beberapa tahun. Kalau layanannya sudah tidak
           ada, hapus seluruh entri ini. */
        harga: { id: "Mulai dari Rp 400.000", en: "From IDR 400,000" },
      },
    ],
  },

  {
    key: "anak",
    judul: { id: "Kamar Anak", en: "Paediatric Rooms" },

    /* ⚠️ TIGA ENTRI TERAKHIR BUKAN KAMAR, MELAINKAN ALAT.

       Blue Light, Inkubator, dan Double Blue Light adalah peralatan
       perawatan bayi - fototerapi untuk bayi kuning, dan inkubator
       untuk bayi yang butuh suhu terjaga. Situs lama menaruhnya
       sederet dengan kelas kamar, jadi urutannya dipertahankan di
       sini.

       Bedanya penting bagi orang tua yang membaca: mereka tidak
       MEMILIH inkubator seperti memilih kelas kamar. Alat itu dipakai
       kalau dokter menilai bayinya membutuhkan, dan biayanya
       ditambahkan di atas biaya kamar - bukan menggantikannya.

       Kalau bagian terkait bisa memastikan cara penagihannya,
       pertimbangkan memisahkannya jadi kategori tersendiri, misalnya
       "Perawatan Bayi", dengan keterangan bahwa biayanya menambah
       biaya kamar. Saya tidak melakukannya sekarang karena itu
       keterangan yang saya tidak punya sumbernya. */
    kamar: [
      {
        nama: { id: "Kelas Satu", en: "Class I" },
        harga: { id: "Mulai dari Rp 315.000", en: "From IDR 315,000" },
      },
      {
        nama: { id: "Kelas Dua", en: "Class II" },
        harga: { id: "Mulai dari Rp 240.000", en: "From IDR 240,000" },
      },
      {
        nama: { id: "Kelas Tiga", en: "Class III" },
        harga: { id: "Mulai dari Rp 155.000", en: "From IDR 155,000" },
      },
      {
        nama: { id: "Kamar Perina", en: "Perinatal Room" },
        harga: { id: "Mulai dari Rp 240.000", en: "From IDR 240,000" },
      },
      {
        nama: { id: "Blue Light", en: "Blue Light" },
        harga: { id: "Mulai dari Rp 265.000 - Rp 315.000", en: "From IDR 265,000 - IDR 315,000" },
      },
      {
        nama: { id: "Inkubator", en: "Incubator" },
        harga: { id: "Mulai dari Rp 350.000", en: "From IDR 350,000" },
      },
      {
        nama: { id: "Double Blue Light", en: "Double Blue Light" },
        harga: { id: "Mulai dari Rp 650.000", en: "From IDR 650,000" },
      },
    ],
  },

  {
    key: "khusus",
    judul: { id: "Perawatan Khusus", en: "Special Care" },

    /* ⚠️ KATEGORI INI BERBEDA SIFATNYA DARI DUA DI ATAS, dan itu
       terlihat di tampilannya lewat `catatan` di bawah.

       Kamar dewasa dan kamar anak DIPILIH pasien atau keluarganya -
       sesuai kemampuan dan kenyamanan. HCU, ICU, PICU, dan NICU tidak.
       Pasien masuk ke sana karena kondisinya menuntut pemantauan
       ketat, dan yang memutuskan adalah dokter yang merawat.

       Karena itu keterangan tambahannya penting: tanpa itu, halaman
       ini terbaca seolah keluarga bisa "memesan" ICU seperti memesan
       VIP Room - dan keluarga yang sedang panik bisa salah paham,
       lalu kecewa saat permintaannya tidak bisa dipenuhi.

       Tombol "Tanya ketersediaan" tetap ada dan tetap berguna:
       ketersediaan tempat tidur ICU justru pertanyaan yang paling
       sering diajukan keluarga pasien, termasuk dari rumah sakit lain
       yang hendak merujuk. */
    catatan: {
      id: "Penempatan di ruang perawatan khusus ditentukan oleh dokter berdasarkan kondisi pasien, bukan atas permintaan.",
      en: "Placement in special care units is determined by the treating doctor based on the patient's condition, not by request.",
    },

    kamar: [
      {
        /* Empat unit disebut dalam satu baris karena tarifnya sama,
           persis seperti di situs lama.

           Kalau tarifnya ternyata berbeda per unit - HCU biasanya
           lebih murah daripada ICU - pisahkan jadi empat entri.
           Menampilkan satu angka untuk empat unit yang harganya
           berbeda akan ditagih keluarga pasien di kemudian hari. */
        nama: { id: "HCU / ICU / PICU / NICU", en: "HCU / ICU / PICU / NICU" },
        harga: { id: "Mulai dari Rp 580.000", en: "From IDR 580,000" },
      },
    ],
  },
];

/* Berlaku untuk seluruh kelas, jadi ditulis sekali di bawah daftar -
   bukan diulang delapan kali seperti di halaman lama. Pengulangan
   sebanyak itu justru membuat orang berhenti membacanya, padahal ini
   keterangan terpenting sebelum masuk. */
export const CATATAN_HARGA = {
  id: "Harga di atas belum termasuk jasa dokter dan tindakan medis, serta dapat berubah sewaktu-waktu.",
  en: "The rates above exclude doctor's fees and medical procedures, and are subject to change.",
};

/* ============================================================
   EKSPOR KESESUAIAN - SEMENTARA, BOLEH DIHAPUS

   Dua ekspor di bawah ada semata-mata supaya versi LAMA
   RawatInapContent.jsx tetap bisa di-build.

   MASALAHNYA. Versi lama komponen itu mengimpor `JUDUL_KAMAR` dan
   `KAMAR`. Berkas ini sudah menggantinya dengan `KATEGORI_KAMAR`,
   jadi begitu berkas data diperbarui tanpa komponennya ikut
   diperbarui, seluruh halaman gagal dengan pesan:

       Export JUDUL_KAMAR doesn't exist in target module

   Dua berkas yang harus diganti bersamaan seperti itu rapuh - cukup
   satu yang tertinggal dan situsnya mati. Dengan dua ekspor di bawah,
   urutannya tidak lagi penting: komponen versi mana pun bisa
   di-build.

   YANG DILIHAT PENGUNJUNG kalau komponennya masih versi lama: seluruh
   lima belas kamar tampil sebagai satu daftar, dan nama kelas yang
   berulang diberi keterangan kategorinya - "Kelas Satu (Kamar Anak)".
   Isinya lengkap, hanya tidak terkelompok.

   HAPUS KEDUANYA setelah RawatInapContent.jsx versi baru terpasang.
   Cara memastikan sudah aman: buka komponennya, cari `JUDUL_KAMAR` -
   kalau tidak ketemu, dua ekspor di bawah tidak dipakai siapa pun
   lagi dan boleh dibuang.
   ============================================================ */

export const JUDUL_KAMAR = { id: "Pilihan Kamar", en: "Room Options" };

export const KAMAR = KATEGORI_KAMAR.flatMap((kategori, i) =>
  kategori.kamar.map((k) => {
    /* Nama kelas berulang antar kategori - "Kelas Satu" ada di kamar
       dewasa maupun kamar anak, dengan harga berbeda. Dalam satu
       daftar datar, dua baris bernama sama dengan harga berbeda
       terbaca seperti kekeliruan, jadi kategorinya ikut disebut.

       Kategori pertama tidak diberi keterangan supaya daftarnya tidak
       jadi penuh tanda kurung tanpa perlu. */
    if (i === 0) return k;
                                                                                        
    return {
      ...k,
      nama: {
        id: `${k.nama.id} (${kategori.judul.id})`,
        en: `${k.nama.en} (${kategori.judul.en})`,
      },
    };
  })
);
